import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  Overline,
  ReasonField,
  TextField,
  cx,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useCsrf,
  useDebounced,
  useReason,
  useToast,
  type ApiError,
} from "@amber/ui";
import { budgetValuesSchema, type BudgetPreview, type BudgetsResponse, type BudgetValues } from "../../contracts/index";
import { budgetPreviewQuery, operationsCommands } from "../queries";
import { AFTER_OPERATIONS_CHANGE, AFTER_OPERATIONS_CONFLICT } from "../keys";
import {
  allowanceTotal,
  differingValues,
  draftErrors,
  draftFromValues,
  draftOf,
  previewKey,
  savedMessage,
  valuesFromKey,
  valuesOf,
  visibleError,
  type Draft,
} from "./budgetDraft";
import { usd } from "./money";
import { Table, Td, Th, ToneIcon } from "./parts";

function Group({
  legend,
  note,
  hint,
  children,
}: {
  legend: string;
  note?: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <fieldset className="m-0 mt-3.5 min-w-0 border-0 p-0">
      <legend className="m-0 p-0 text-base font-semibold">
        {legend}
        {note ? <span className="ml-2 text-md font-normal text-muted">{note}</span> : null}
      </legend>
      <div className="grid grid-cols-3 gap-x-3 max-[560px]:grid-cols-1 [&>div]:mt-2">{children}</div>
      {hint ? <p className="m-0 mt-1.5 text-md text-muted">{hint}</p> : null}
    </fieldset>
  );
}

function useBudgetPreview(values: BudgetValues | null) {
  const csrf = useCsrf();
  const key = useDebounced(previewKey(values), 300);
  return useQuery(budgetPreviewQuery(valuesFromKey(key), csrf()));
}

function EffectLines({
  values,
  p,
  failed,
  error,
}: {
  values: BudgetValues | null;
  p: BudgetPreview | undefined;
  failed: boolean;
  error: unknown;
}) {
  if (!values) return <p className="m-0 text-muted">Correct the highlighted values to see the effect.</p>;
  if (p)
    return (
      <>
        {p.invalid ? (
          <p role="alert" className="m-0 flex items-start gap-1.5 font-strong text-attention">
            <ToneIcon tone="failed" />
            {p.invalid}
          </p>
        ) : null}
        {p.effects.map((e) => (
          <p key={e.text} className={cx("m-0 flex items-start gap-1.5", e.tone === "ok" ? "text-muted" : "text-fg")}>
            <ToneIcon tone={e.tone} />
            {e.text}
          </p>
        ))}
        {p.resumes > 0 ? (
          <p className="m-0 flex items-start gap-1.5 font-strong text-fg">
            <ToneIcon tone="ok" />
            {p.resumes === 1
              ? "1 item waiting for Budget would resume"
              : `${String(p.resumes)} items waiting for Budget would resume`}
          </p>
        ) : null}
      </>
    );
  if (failed) return <p className="m-0 text-attention">Couldn&apos;t work out the effect. {errorText(error)}</p>;
  return <p className="m-0 text-muted">Working out the effect…</p>;
}

function Changes({ p }: { p: BudgetPreview | undefined }) {
  return (
    <div className="mt-3">
      <Overline as="h3" spacing="narrow" className="m-0 mb-1.5">
        Changes
      </Overline>
      <ul aria-label="Changes" className="m-0 flex list-none flex-col gap-1 p-0 text-md">
        {p && p.changes.length > 0 ? (
          p.changes.map((c) => (
            <li key={c.label}>
              {c.label}: <del className="text-muted decoration-attention">{c.before}</del>{" "}
              <span className="text-muted">→</span> <ins className="font-strong no-underline">{c.after}</ins>
            </li>
          ))
        ) : (
          <li className="text-muted">No changes yet</li>
        )}
      </ul>
    </div>
  );
}

function SaveProblem({
  conflict,
  savedDraft,
  serverValues,
  error,
  onCompare,
  onKeep,
}: {
  conflict: ApiError | null;
  savedDraft: Draft | null;
  serverValues: string | null;
  error: unknown;
  onCompare: () => void;
  onKeep: () => void;
}) {
  if (conflict)
    return (
      <Callout
        tone="waiting"
        role="alert"
        className="mt-3"
        title="Another Admin saved budgets and limits since you opened them"
        actions={
          <>
            <Button
              size="sm"
              disabledReason={savedDraft ? null : "The saved values couldn't be loaded"}
              onClick={onCompare}
            >
              Compare…
            </Button>
            <Button size="sm" variant="primary" onClick={onKeep}>
              Keep my draft
            </Button>
          </>
        }
      >
        {conflict.changedBy ?? "Someone"} saved revision {conflict.currentRevision ?? "?"}
        {conflict.changedAt ? ` at ${formatDateTime(conflict.changedAt)}` : ""}. Your draft is kept until you choose.
      </Callout>
    );
  if (serverValues)
    return (
      <Callout tone="attention" role="alert" className="mt-3">
        {serverValues}
      </Callout>
    );
  return <FailedCallout error={error} fieldsShown />;
}

function CompareSaved({
  savedDraft,
  draft,
  onRebase,
}: {
  savedDraft: Draft;
  draft: Draft;
  onRebase: (next: Draft | null) => void;
}) {
  return (
    <div className="mt-3">
      <Table
        label="Saved values and your draft"
        head={
          <>
            <Th>Value</Th>
            <Th right>Saved now</Th>
            <Th right>Your draft</Th>
          </>
        }
      >
        {differingValues(savedDraft, draft).map((c) => {
          const show = (v: string) => (c.unit === "usd" ? usd(Number(v)) : `${v}%`);
          return (
            <tr key={c.key}>
              <Td className="text-fg">{c.label}</Td>
              <Td right className="tabular-nums">
                {show(savedDraft[c.key])}
              </Td>
              <Td right className="tabular-nums text-fg">
                {show(draft[c.key])}
              </Td>
            </tr>
          );
        })}
      </Table>
      <div className="mt-2 flex flex-wrap justify-end gap-1.5">
        <Button
          size="sm"
          onClick={() => {
            onRebase(savedDraft);
          }}
        >
          Use saved values
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            onRebase(null);
          }}
        >
          Keep my draft
        </Button>
      </div>
    </div>
  );
}

export function EditBudgetsDialog({ data, onClose }: { data: BudgetsResponse; onClose: () => void }) {
  const toast = useToast();
  const [initial] = useState(() => draftOf(data));
  const [base, setBase] = useState(data.revision);
  const [draft, setDraft] = useState<Draft>(initial);
  const [comparing, setComparing] = useState(false);
  const errors = draftErrors(draft);
  const values = Object.keys(errors).length === 0 ? valuesOf(draft) : null;
  const preview = useBudgetPreview(values);
  const p = preview.data;
  const mutation = useCommandMutation({
    run: (token, text: string) =>
      values
        ? operationsCommands.editBudgets({ base_revision: base, values, reason: text }, token)
        : Promise.reject(new Error("Correct the highlighted values")),
    invalidate: AFTER_OPERATIONS_CHANGE,
    invalidateOnConflict: AFTER_OPERATIONS_CONFLICT,
    onSuccess: (result) => {
      toast.show(savedMessage(result));
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict" ? mutation.error : null;
  const saved = conflict ? budgetValuesSchema.safeParse(conflict.current) : null;
  const savedDraft = saved?.success ? draftFromValues(saved.data) : null;
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const submitted = reason.submitted;
  const serverValues = fieldErrors["values"] ?? null;
  const total = allowanceTotal(draft);
  const totalNow = allowanceTotal(initial);
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => {
    setDraft({ ...draft, [k]: e.target.value });
  };
  const rebase = (next: Draft | null) => {
    if (conflict?.currentRevision) setBase(conflict.currentRevision);
    if (next) setDraft(next);
    setComparing(false);
    mutation.reset();
  };
  const field = (k: keyof Draft, label: string) => (
    <TextField
      label={label}
      inputMode="decimal"
      autoComplete="off"
      value={draft[k]}
      error={visibleError(k, { draft, initial, errors, submitted })}
      onChange={set(k)}
    />
  );
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Edit budgets and limits"
      onSubmit={() => {
        if (!reason.submit() || !values || conflict || p?.invalid) return;
        mutation.mutate(reason.trimmed);
      }}
      footer={
        <>
          <span className="mr-auto self-center text-md text-muted max-[560px]:hidden">
            Recorded in the revision history and the audit log
          </span>
          <DialogFooter
            onClose={onClose}
            confirm="Save changes"
            pending={mutation.isPending}
            failed={mutation.isError && !conflict}
          />
        </>
      }
    >
      <DialogText>Changes apply immediately and are recorded as a new revision.</DialogText>
      <Group
        legend="Monthly allowances"
        note="USD"
        hint={
          Number.isFinite(total)
            ? `Total ${usd(total)} a month${total === totalNow ? "" : ` (now ${usd(totalNow)})`}`
            : null
        }
      >
        {field("infrastructure", "Infrastructure")}
        {field("ai", "AI")}
        {field("other", "Other")}
      </Group>
      <Group
        legend="Thresholds"
        note="% of each allowance, spent and committed"
        hint="Optional commitments always stop before projected spend reaches 100%."
      >
        {field("alert", "Alert at (%)")}
        {field("suspend", "Suspend at (%)")}
      </Group>
      <Group
        legend="Essential-service reserve"
        note="% of each category allowance"
        hint="Lets fresh discovery and archiving of new originals continue after an allowance is reached. Preserving existing evidence always continues; transcription and speaker naming wait."
      >
        {field("reserve", "Reserve (%)")}
      </Group>
      <Group legend="Limits">
        {field("daily", "Daily spend (USD)")}
        {field("perJob", "Per job (USD)")}
        {field("backfill", "Backfill share (%)")}
      </Group>
      <div className="mt-4">
        <Overline as="h3" spacing="narrow" className="m-0 mb-1.5">
          Effect now
        </Overline>
        <div
          aria-live="polite"
          className="flex flex-col gap-1 rounded-lg border border-divider bg-surface px-3 py-2.5 text-md"
        >
          <EffectLines values={values} p={p} failed={preview.isError} error={preview.error} />
        </div>
      </div>
      <Changes p={p} />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <SaveProblem
        conflict={conflict}
        savedDraft={savedDraft}
        serverValues={serverValues}
        error={mutation.error}
        onCompare={() => {
          setComparing(true);
        }}
        onKeep={() => {
          rebase(null);
        }}
      />
      {conflict && comparing && savedDraft ? (
        <CompareSaved savedDraft={savedDraft} draft={draft} onRebase={rebase} />
      ) : null}
    </Dialog>
  );
}
